/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Component, { ComponentAttrs } from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import highlight from 'flarum/common/helpers/highlight';
import icon from 'flarum/common/helpers/icon';
import ItemList from 'flarum/common/utils/ItemList';
import app from 'flarum/forum/app';
import type Mithril from 'mithril';
import type Article from '../models/Article';

export interface ArticleListItemAttrs extends ComponentAttrs {
	article: Article;
	searchQuery?: string;
	onSelect?: (article: Article) => void;
	selected?: boolean;
	multiSelectable?: boolean;
	size?: string;
}

/**
 * Selectable when rendered from the ArticlePickerModal, which is what passes `onSelect`.
 */
export default class ArticleListItem extends Component<ArticleListItemAttrs> {
	view() {
		const { article, onSelect, selected } = this.attrs;

		return (
			<li>
				<div className="ArticleListItem">
					<Link
						href={app.route.of_article(article)}
						className={selected ? 'selected' : ''}
						onclick={
							onSelect
								? (e: Event) => {
										e.preventDefault();
										onSelect(article);
									}
								: undefined
						}
					>
						{this.items().toArray()}
						{this.iconName && <div className="ArticleListItem--select_icon">{icon(this.iconName)}</div>}
					</Link>
				</div>
			</li>
		);
	}

	get iconName(): string | undefined {
		const { onSelect, selected, multiSelectable } = this.attrs;

		let iconName;
		if (onSelect) {
			if (multiSelectable) {
				if (selected) {
					iconName = 'far fa-check-square';
				} else {
					iconName = 'far fa-square';
				}
			} else {
				if (selected) {
					iconName = 'far fa-dot-circle';
				} else {
					iconName = 'far fa-circle';
				}
			}
		}
		return iconName;
	}

	items(): ItemList<Mithril.Children> {
		const items = new ItemList<Mithril.Children>();

		const { article, size = 'small' } = this.attrs;

		items.add(
			'cover',
			<div className={`ArticleListItem-cover ArticleCover--${size}`} style={{ backgroundImage: `url("${article.coverImage()}")` }}></div>,
			50
		);

		items.add('infos', <div className="ArticleListItem-infos">{this.infoItems().toArray()}</div>, 40);

		return items;
	}

	infoItems(): ItemList<Mithril.Children> {
		const items = new ItemList<Mithril.Children>();

		const { article, searchQuery } = this.attrs;

		let highlightRegExp;
		if (searchQuery) {
			highlightRegExp = new RegExp(`${searchQuery}|${searchQuery.trim().replace(/\s+/g, '|')}`, 'gi');
		}

		const attrs = { author: 10, title: 8, subtitle: 6 } as const satisfies Record<string, number>;
		for (const attr of Object.keys(attrs) as (keyof typeof attrs)[]) {
			article[attr]() &&
				items.add(
					attr,
					<div className={`ArticleListItem-${attr}`}>{highlight(article[attr](), highlightRegExp)}</div>,
					attrs[attr] // priority
				);
		}

		return items;
	}
}
